import {Edit} from "@mui/icons-material";
import React, {useEffect} from 'react';
import {ReferenceDefinitionDto} from "../../../../../common/apis/stereotype/ReferenceDefinitionDto";
import {StereotypeDto} from "../../../../../common/apis/stereotype/StereotypeDto";
import Dialog from '../../../../../components/dialogs/Dialog';
import DialogContent from '../../../../../components/dialogs/DialogContent';
import DialogTitle from '../../../../../components/dialogs/DialogTitle';
import Grid from '../../../../../components/dialogs/Grid';
import Checkbox from "../../../../../components/fields/Checkbox";
import MultiValueTextField from "../../../../../components/fields/textfield/MultiValueTextField";
import TextField from '../../../../../components/fields/textfield/TextField';
import SectionActionBar from "../../../../../components/section/SectionActionBar";
import {SectionActionButton} from "../../../../../components/section/SectionActionButton";
import SectionHeader from "../../../../../components/section/SectionHeader";
import {t} from '../../../../../store/localization/TranslMessasge';
import {CommonTranslation} from '../../CommonTranslation';
import {RelationshipTypeFormatter} from "../../relationship/RelationshipTypeFormatter";
import {StereotypesTranslationKey} from "../StereotypesTranslationKey";
import {useFindReferenceDefinitionByIdQuery} from "./ReferenceDefinitionQuery";
import {ReferenceDefinitionUpdateDialog} from "./ReferenceDefinitionUpdateDialog";
import {ReferenceElementFilterFormatter} from "./ReferenceElementFilterFormatter";
import {ReferenceRelationshipDirectionFormatter} from "./ReferenceRelationshipDirectionFormatter";
import {RelatedElementFilterUpdateDialog} from "./RelatedElementFilterUpdateDialog";

interface ReferenceDefinitionDetailDialogProps {
    open: boolean;
    stereotypeId: string;
    stereotypes: StereotypeDto[];
    referenceDefinition: ReferenceDefinitionDto;
    onClose: (updated: boolean) => void;
}

export function ReferenceDefinitionDetailDialog(props: Readonly<ReferenceDefinitionDetailDialogProps>) {
    const { open, stereotypeId, stereotypes, referenceDefinition: initialReferenceDef, onClose } = props;
    const [updated, setUpdated] = React.useState(false);
    const [updateDialogOpen, setUpdateDialogOpen] = React.useState(false);
    const [filterUpdateDialogOpen, setFilterUpdateDialogOpen] = React.useState(false);

    const {data: referenceDef = initialReferenceDef, refetch} = useFindReferenceDefinitionByIdQuery(stereotypeId, initialReferenceDef.id);

    useEffect(() => {
        if (open) {
            setUpdated(false);
        }
    }, [open]);

    function onDialogClose() {
        onClose(updated);
    }

    function onUpdated() {
        setUpdated(true);
        refetch();
    }

    if (!referenceDef || !stereotypes) {
        return null;
    }

    return (
            <>
                <Dialog open={open} onClose={onDialogClose} maxWidth="md">
                    <DialogTitle title={t(StereotypesTranslationKey.SMART_REFERENCE)}
                            onDialogClosed={onDialogClose} />
                    <DialogContent>
                        <Grid container>
                            <Grid item xs={12}>
                                <SectionHeader title={t(StereotypesTranslationKey.DETAILS)}>
                                    <SectionActionBar>
                                        <SectionActionButton
                                                icon={<Edit fontSize={"inherit"}/>}
                                                tooltip={t(CommonTranslation.EDIT)}
                                                onClick={() => setUpdateDialogOpen(true)}
                                                visible={referenceDef.acl?.canUpdate}/>
                                    </SectionActionBar>
                                </SectionHeader>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextField
                                        label={t(StereotypesTranslationKey.NAME)}
                                        value={referenceDef.name}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12} sm={12} md={5}>
                                <TextField
                                        label={t(StereotypesTranslationKey.RELATIONSHIP_TYPE)}
                                        value={RelationshipTypeFormatter.formatRelationshipType(referenceDef.relationshipType)}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                        label={t(StereotypesTranslationKey.RELATIONSHIP_DIRECTION)}
                                        value={ReferenceRelationshipDirectionFormatter.formatDirection(referenceDef.relationshipDirection, t)}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Checkbox
                                        label={t(StereotypesTranslationKey.MULTIPLE)}
                                        checked={referenceDef.multiple}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                        label={t(CommonTranslation.DESCRIPTION)}
                                        value={referenceDef.description ?? ''}
                                        readOnly={true}
                                        multiline
                                        rows={4}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Checkbox
                                        label={t(StereotypesTranslationKey.SMART_REFERENCE_REQUIRED)}
                                        checked={referenceDef.required}
                                        readOnly={true}/>
                            </Grid>

                            <Grid item xs={12}>
                                <SectionHeader title={t(StereotypesTranslationKey.ELEMENT_FILTER)}>
                                    <SectionActionBar>
                                        <SectionActionButton
                                                icon={<Edit fontSize={"inherit"}/>}
                                                tooltip={t(CommonTranslation.EDIT)}
                                                onClick={() => setFilterUpdateDialogOpen(true)}
                                                visible={referenceDef.acl?.canUpdate}/>
                                    </SectionActionBar>
                                </SectionHeader>
                            </Grid>
                            <Grid item xs={12}>
                                <MultiValueTextField
                                        label={t(StereotypesTranslationKey.ELEMENT_FILTER)}
                                        values={ReferenceElementFilterFormatter.formatFilteringCriteria(referenceDef.relatedElementFilter, stereotypes, t)}
                                        readOnly={true}/>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </Dialog>

                <ReferenceDefinitionUpdateDialog
                        open={updateDialogOpen}
                        stereotypeId={stereotypeId}
                        referenceDefinition={referenceDef}
                        onUpdated={onUpdated}
                        onClose={() => setUpdateDialogOpen(false)}/>
                
                <RelatedElementFilterUpdateDialog
                        open={filterUpdateDialogOpen}
                        stereotypeId={stereotypeId}
                        referenceDefinition={referenceDef}
                        onUpdated={onUpdated}
                        onClose={() => setFilterUpdateDialogOpen(false)}/>
            </>
    );
}